import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, Trash2, KeyRound } from "lucide-react";

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ name: "", email: "", role: "staff", password: "" });
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState("");

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const createUser = async () => {
    try {
      await api.post("/users", invite);
      toast.success(`Account created for ${invite.email}. Share the password with them privately.`);
      setInviteOpen(false);
      setInvite({ name: "", email: "", role: "staff", password: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeUser = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.email} no longer has access.`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doReset = async () => {
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { password: resetPassword });
      toast.success("Password updated.");
      setResetTarget(null);
      setResetPassword("");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-8" data-testid="team-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-serif font-semibold">Team</h1>
          <p className="text-muted-foreground mt-2">Only people you add here can sign in. There is no public sign-up.</p>
        </div>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-btn"><UserPlus className="h-4 w-4 mr-2" /> Add a team member</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Add a team member</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Full name</Label><Input data-testid="invite-name-input" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input data-testid="invite-email-input" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                  <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff — can view records and run reports</SelectItem>
                    <SelectItem value="admin">Admin — full access</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Temporary password</Label>
                <Input data-testid="invite-password-input" type="text" value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} />
                <p className="text-xs text-muted-foreground">At least 10 characters, with an uppercase letter, a lowercase letter, and a number.</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={createUser} data-testid="create-user-btn">Create account</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="hidden sm:table-cell">Added</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="py-4 font-medium">{u.name}{u.id === user?.id && <span className="text-xs text-muted-foreground ml-2">(you)</span>}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${u.role === "admin" ? "bg-primary/10 text-primary" : "bg-secondary text-secondary-foreground"}`}>
                      {u.role === "admin" ? "Administrator" : "Staff"}
                    </span>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setResetTarget(u)} title="Reset password" data-testid={`reset-password-btn-${u.id}`}>
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      {u.id !== user?.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" data-testid={`delete-user-btn-${u.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="font-serif">Remove {u.name}'s access?</AlertDialogTitle>
                              <AlertDialogDescription>They will no longer be able to sign in. You can add them back later.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep their access</AlertDialogCancel>
                              <AlertDialogAction onClick={() => removeUser(u)} className="bg-destructive hover:bg-destructive/90" data-testid={`confirm-delete-user-${u.id}`}>Yes, remove them</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Reset password for {resetTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <Input type="text" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} data-testid="reset-password-input" />
            <p className="text-xs text-muted-foreground">At least 10 characters, with an uppercase letter, a lowercase letter, and a number.</p>
          </div>
          <DialogFooter>
            <Button onClick={doReset} data-testid="confirm-reset-password-btn">Update password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
